import { Colors, Container, Heading, Icon, Text } from '@amelio/ui-library';

import type { ColorType, IconName } from '@amelio/ui-library';
import type { ReactNode } from 'react';

/** The card's own column, narrower than the pages it closes: 1152px, in real pixels because the
 *  `--container-*` tokens are still read off the host's 14px root. */
const CARD_SHELL =
    'mx-auto flex max-w-[1152px] flex-col items-center p-7 text-center md:p-9 lg:p-13';

/** The floating disc that holds the mark of the moment: 128px. */
const MEDALLION_DISC =
    'border-primary/30 motion-safe:animate-bounce-soft flex size-32 items-center justify-center rounded-full border-4 bg-white shadow-xl';

/** The mark inside the disc, at half its width. Given as a number so `Icon` sizes itself inline
 *  and no caller has to out-shout its own size class. */
const MEDALLION_MARK_SIZE = 64;

interface MeetingCompletionCardProps {
    /** The subject's own mark: a handshake closes a meeting, sparkles close an assessment. */
    icon: { name: IconName; color: ColorType };
    title: string;
    /** The line the card opens on, above its body. A screen without one says nothing here. */
    subtitle?: string;
    description: string;
    /** The one action that ends the screen, and whatever has to be said under it. */
    children: ReactNode;
    /** Where the page places the card; the card itself owns nothing outside its own edge. */
    className?: string;
    dataTestId?: string;
}

/**
 * The closing moment of a meeting: a floating disc, the words that mark the moment, and the action
 * that ends it. The assessment journey and the thematic meetings share this one design and keep
 * their own wording, so neither screen can drift from the other.
 */
export const MeetingCompletionCard = ({
    icon,
    title,
    subtitle,
    description,
    children,
    className = '',
    dataTestId,
}: MeetingCompletionCardProps) => (
    <Container
        border={'white'}
        borderRadius={'2xl'}
        background={'translucent'}
        dataTestId={dataTestId}
        className={`${CARD_SHELL} ${className}`}
    >
        <div className={MEDALLION_DISC}>
            <Icon
                name={icon.name}
                color={icon.color}
                size={MEDALLION_MARK_SIZE}
                ariaHidden
            />
        </div>

        <Heading
            level={3}
            weight={'bold'}
            color={Colors.NAVY}
            align={'center'}
            className={'mt-9 text-2xl md:text-3xl lg:text-5xl'}
        >
            {title}
        </Heading>

        {subtitle && (
            <Text
                weight={'semibold'}
                color={Colors.PRIMARY_400}
                align={'center'}
                className={'mt-4 text-xl md:text-2xl'}
            >
                {subtitle}
            </Text>
        )}

        {/* One step under the library's `large`: the card's body reads as a lead, not as a second
            title. The module's own default would be two steps down, so the step is named here. */}
        <Text
            size={'default'}
            color={Colors.BASE_400}
            align={'center'}
            className={'mx-auto mt-4 max-w-3xl whitespace-pre-line'}
        >
            {description}
        </Text>

        <div className={'mt-9 flex flex-col items-center gap-3'}>{children}</div>
    </Container>
);
